import { Link } from "react-router-dom";
import { Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LegalFooter } from "@/components/LegalFooter";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 py-8 flex flex-col">
      <div className="mx-auto w-full max-w-4xl space-y-6 flex-1">
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/">Back to Workshop</Link>
          </Button>
        </div>

        <section className="glass-panel rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10">
              <Hammer className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Terms of Service</h1>
              <p className="text-sm text-muted-foreground">
                Welcome to The Workshop, located at
                {" "}
                <a className="underline" href="https://theworkshop.space101.store/" target="_blank" rel="noreferrer">
                  https://theworkshop.space101.store/
                </a>
                .
                These Terms of Service ("Terms") govern your access to and use of our website, tools, and services designed to assist FiveM developers in creating and developing vehicles and related assets.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                By accessing or using our services, you agree to comply with these Terms.
              </p>
            </div>
          </div>
        </section>

        <section className="glass-panel rounded-2xl p-6 space-y-5">
          <div>
            <h2 className="text-base font-medium text-foreground">1. Acceptance of Terms</h2>
            <p className="text-sm text-muted-foreground mt-2">
              By accessing, purchasing, downloading, or using any products, services, or tools provided by The Workshop, you agree to these Terms of Service.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              If you do not agree with these Terms, you must not use our services.
            </p>
            <p className="text-sm text-muted-foreground mt-2">We reserve the right to modify these Terms at any time.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Continued use of the website after changes are posted constitutes acceptance of those changes.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">2. Description of Services</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The Workshop provides digital tools and services intended to assist developers working with FiveM in creating and developing vehicles.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Tools may include software, utilities, resources, and development aids.
            </p>
            <p className="text-sm text-muted-foreground mt-2">All services are provided as-is.</p>
            <p className="text-sm text-muted-foreground mt-2">We may update, modify, or discontinue services at any time without notice.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">3. User Accounts</h2>
            <p className="text-sm text-muted-foreground mt-2">To access certain features, users may be required to create an account.</p>
            <p className="text-sm text-muted-foreground mt-2">Users agree that:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Account information must be accurate and truthful.</li>
              <li>You are responsible for maintaining account security.</li>
              <li>You are responsible for all actions taken under your account.</li>
              <li>The Workshop is not responsible for unauthorized access caused by poor account security.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">4. License to Use Our Tools</h2>
            <p className="text-sm text-muted-foreground mt-2">
              When purchasing or accessing tools from The Workshop, you are granted a limited, non-transferable, non-exclusive license to use the software.
            </p>
            <p className="text-sm text-muted-foreground mt-2">You may not:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Resell our software or tools.</li>
              <li>Redistribute our tools.</li>
              <li>Upload them to file sharing sites or repositories.</li>
              <li>Share them publicly or privately without permission.</li>
              <li>Reverse engineer or attempt to bypass protections.</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">Violation of these terms may result in immediate account termination.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">5. IP Lock &amp; Account Sharing Policy</h2>
            <p className="text-sm text-muted-foreground mt-2">
              To protect our software, some products use IP locking or license verification systems.
            </p>
            <p className="text-sm text-muted-foreground mt-2">The following actions are strictly prohibited:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Attempting to bypass or manipulate IP lock protections.</li>
              <li>Using multiple users on one account.</li>
              <li>Sharing accounts across teams, organizations, or friends.</li>
              <li>Attempting to spoof or manipulate authentication systems.</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">If abuse is detected:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>The account may be immediately suspended or permanently deactivated.</li>
              <li>Access to all products may be revoked.</li>
              <li>Future purchases may be blocked.</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">Accounts terminated due to abuse will not receive refunds.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">6. Payments &amp; Refund Policy</h2>
            <p className="text-sm text-muted-foreground mt-2">Due to the nature of digital products, all purchases are generally final.</p>
            <p className="text-sm text-muted-foreground mt-2">Refunds will not be issued for:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Account sharing</li>
              <li>IP lock abuse</li>
              <li>Attempting to bypass licensing systems</li>
              <li>Violating these Terms</li>
              <li>Unauthorized redistribution of tools</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">We reserve the right to deny refund requests at our discretion.</p>
            <h3 className="text-sm font-medium text-foreground mt-4">AI tokens</h3>
            <p className="text-sm text-muted-foreground mt-2">
              AI tokens granted as part of a paid subscription expire 30 days after they are granted, and unused
              subscription tokens do not roll over. AI tokens purchased separately as top-up packs do not expire.
              AI tokens are non-refundable in all cases, whether granted with a subscription or purchased as a
              top-up. Cancelling or downgrading a subscription does not entitle the subscriber to a refund of
              any tokens already granted.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">7. Anti-Leak / Anti-Redistribution Policy</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The Workshop strictly prohibits leaking, sharing, or redistributing any of our software or tools.
            </p>
            <p className="text-sm text-muted-foreground mt-2">Prohibited actions include:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Uploading our tools to leak forums or piracy sites</li>
              <li>Sharing download files with unauthorized users</li>
              <li>Repackaging our tools for redistribution</li>
              <li>Selling access to our tools without permission</li>
              <li>Posting our software publicly on GitHub or similar platforms</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">If we detect leaked copies or redistribution:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>The associated account will be permanently banned.</li>
              <li>All licenses will be revoked immediately.</li>
              <li>Access to future products will be denied.</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              We reserve the right to pursue legal action, DMCA takedowns, and license termination against individuals or organizations distributing our software without authorization.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">8. Intellectual Property</h2>
            <p className="text-sm text-muted-foreground mt-2">All content provided by The Workshop is owned by The Workshop or its creators.</p>
            <p className="text-sm text-muted-foreground mt-2">This includes but is not limited to:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Software</li>
              <li>Code</li>
              <li>Tools</li>
              <li>Documentation</li>
              <li>Graphics</li>
              <li>Branding</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">Unauthorized copying or distribution is strictly prohibited.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">9. Disclaimer of Warranty</h2>
            <p className="text-sm text-muted-foreground mt-2">All services and tools are provided "as is" and "as available."</p>
            <p className="text-sm text-muted-foreground mt-2">We do not guarantee:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Continuous service availability</li>
              <li>Compatibility with all systems</li>
              <li>Error-free operation</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">Users assume all risks associated with using the tools.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">10. Limitation of Liability</h2>
            <p className="text-sm text-muted-foreground mt-2">To the fullest extent permitted by law, The Workshop shall not be liable for:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Data loss</li>
              <li>Financial loss</li>
              <li>Lost development time</li>
              <li>Indirect or consequential damages</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">resulting from the use or inability to use our services.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">11. Termination</h2>
            <p className="text-sm text-muted-foreground mt-2">We reserve the right to terminate or suspend accounts without prior notice if users:</p>
            <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
              <li>Violate these Terms</li>
              <li>Share accounts</li>
              <li>Abuse IP lock systems</li>
              <li>Leak or redistribute our tools</li>
              <li>Attempt to pirate or bypass licensing systems</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">Termination may result in permanent loss of access to purchased tools.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">12. Governing Law</h2>
            <p className="text-sm text-muted-foreground mt-2">These Terms are governed by the laws of the United States.</p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">13. Contact</h2>
            <p className="text-sm text-muted-foreground mt-2">If you have questions about these Terms, you can contact us:</p>
            <p className="text-sm text-muted-foreground mt-2">
              Email:
              {" "}
              <a className="underline" href="mailto:info@space101.store">info@space101.store</a>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Website:
              {" "}
              <a className="underline" href="https://theworkshop.space101.store/" target="_blank" rel="noreferrer">
                https://theworkshop.space101.store/
              </a>
            </p>
          </div>
        </section>
      </div>
      <LegalFooter />
    </div>
  );
}
